import { Inject, Injectable } from '@angular/core';
import { RoleType } from 'src/@shared/shared/enum';
import { findSettingsPermissionGroup } from 'src/@shared/shared/permission-header.util';
import {
  LOCAL_STORAGE_PORT,
  StoragePort,
} from 'src/@shared/shared/providers';

const SETTINGS_MODULE_TYPES = ['Paybev'];

@Injectable({
  providedIn: 'root',
})
export class AccessControlService {
  /** Modules that unlock the Paybev Reports entry (Summary icon / top REPORTS nav). */
  private static readonly REPORT_ENTRY_MODULES = [
    'Account Statements',
    'Activity Export',
    'Outstanding Statement',
    'Internal Reports',
    'Projections',
    'Reports', // legacy single-module shape
  ] as const;

  private static readonly REPORT_LANDING_PATH_BY_MODULE: Record<string, string> = {
    'Account Statements': 'report-account-statements',
    'Activity Export': 'report-activity-export',
    'Outstanding Statement': 'report-outstanding-statement',
    'Internal Reports': 'report-internal-reports',
    Projections: 'report-projections',
    Reports: 'report-account-statements',
  };

  private rolePermissions: any[] = [];
  private supplierRolePermissions: any[] = [];

  constructor(@Inject(LOCAL_STORAGE_PORT) private localStoragePort: StoragePort) {}

  canAccessUsers() {
    this.loadRolePermissions();
    let canViewUsers, canAddUser, canEditUser, canDeleteUser;
    const adminDetails = findSettingsPermissionGroup(this.rolePermissions, SETTINGS_MODULE_TYPES);
    const index = adminDetails?.permissions?.findIndex((obj: any) => obj.module == 'Users');
    if (index != undefined && index != -1) {
      canViewUsers = adminDetails.permissions[index].canView;
      canAddUser = adminDetails.permissions[index].canAdd;
      canEditUser = adminDetails.permissions[index].canEdit;
      canDeleteUser = adminDetails.permissions[index].canDelete;
    }
    return { canViewUsers, canAddUser, canEditUser, canDeleteUser };
  }

  canAccessPayments() {
    this.loadRolePermissions();
    let canViewPayment, canAddPayment, canDeletePayment;
    const paymentDetails = this.rolePermissions[3];
    const leaf = paymentDetails?.permissions?.find((obj: any) => obj.module === 'Expenses');
    if (leaf) {
      canViewPayment = leaf?.canView;
      canAddPayment = leaf?.canAdd;
      canDeletePayment = leaf?.canDelete;
    }
    return { canViewPayment, canAddPayment, canDeletePayment };
  }

  canAccessInvestments() {
    this.loadRolePermissions();
    let canViewInvestments, canAddInvestment, canEditInvestment, canDeleteInvestment;
    const investmentDetails = this.rolePermissions[2];
    const leaf = investmentDetails?.permissions?.find((obj: any) => obj.module === 'Investments' || obj.module === 'Investment');
    if (leaf) {
      canViewInvestments = leaf?.canView;
      canAddInvestment = leaf?.canAdd;
      canEditInvestment = leaf?.canEdit;
      canDeleteInvestment = leaf?.canDelete;
    }
    return { canViewInvestments, canAddInvestment, canEditInvestment, canDeleteInvestment };
  }

  canAccessSummary() {
    this.loadRolePermissions();
    let canViewSummary, canAddSummary, canDeleteSummary;
    const summaryDetails = this.rolePermissions[0];
    const index = summaryDetails?.permissions?.findIndex((obj: any) => obj.module == 'Summary');
    if (index != undefined && index != -1) {
      canViewSummary = summaryDetails.permissions[index].canView;
      canAddSummary = summaryDetails.permissions[index].canAdd;
      canDeleteSummary = summaryDetails.permissions[index].canDelete;
    }
    return { canViewSummary, canAddSummary, canDeleteSummary };
  }

  canAccessActivity() {
    this.loadRolePermissions();
    let canViewActivity, canAddActivity;
    const activityDetails = this.rolePermissions[1];
    const index = activityDetails?.permissions?.findIndex((obj: any) => obj.module == 'Activity');
    if (index != undefined && index != -1) {
      canViewActivity = activityDetails.permissions[index].canView;
      canAddActivity = activityDetails.permissions[index].canAdd;
    }
    return { canViewActivity, canAddActivity };
  }

  canAccessInvestors() {
    this.loadRolePermissions();
    let canViewInvestors, canAddInvestor, canDeleteInvestor;
    const adminDetails = findSettingsPermissionGroup(this.rolePermissions, SETTINGS_MODULE_TYPES);
    const leaf = adminDetails?.permissions?.find((obj: any) => obj.module === 'Investors');
    if (leaf) {
      canViewInvestors = leaf?.canView;
      canAddInvestor = leaf?.canAdd;
      canDeleteInvestor = leaf?.canDelete;
    }
    return { canViewInvestors, canAddInvestor, canDeleteInvestor };
  }

  canAccessVendors() {
    this.loadRolePermissions();
    let canViewVendors, canAddVendor, canDeleteVendor;
    const adminDetails = findSettingsPermissionGroup(this.rolePermissions, SETTINGS_MODULE_TYPES);
    const leaf = adminDetails?.permissions?.find((obj: any) => obj.module === 'Vendors');
    if (leaf) {
      canViewVendors = leaf?.canView;
      canAddVendor = leaf?.canAdd;
      canDeleteVendor = leaf?.canDelete;
    }
    return { canViewVendors, canAddVendor, canDeleteVendor };
  }

  canAccessProgramsTags() {
    this.loadRolePermissions();
    let canViewProgramsTags, canAddProgramTag, canEditProgramTag, canDeleteProgramTag;
    const adminDetails = findSettingsPermissionGroup(this.rolePermissions, SETTINGS_MODULE_TYPES);
    const leaf = adminDetails?.permissions?.find((obj: any) => obj.module === 'Program Periods');
    if (leaf) {
      canViewProgramsTags = leaf?.canView;
      canAddProgramTag = leaf?.canAdd;
      canEditProgramTag = leaf?.canEdit;
      canDeleteProgramTag = leaf?.canDelete;
    }
    return { canViewProgramsTags, canAddProgramTag, canEditProgramTag, canDeleteProgramTag };
  }

  /**
   * Tags is its own permission once the registry split lands. Program period screens keep using
   * canAccessProgramsTags(); only the tag grid, route and menu item read this one.
   */
  canAccessTags() {
    this.loadRolePermissions();
    let canViewTags, canAddTag, canEditTag, canDeleteTag;
    const adminDetails = findSettingsPermissionGroup(this.rolePermissions, SETTINGS_MODULE_TYPES);
    const leaf = adminDetails?.permissions?.find((obj: any) => obj.module === 'Tags');
    if (leaf) {
      canViewTags = leaf?.canView;
      canAddTag = leaf?.canAdd;
      canEditTag = leaf?.canEdit;
      canDeleteTag = leaf?.canDelete;
    }
    return { canViewTags, canAddTag, canEditTag, canDeleteTag };
  }

  canAccessTutorials() {
    this.loadRolePermissions();
    let canViewTutorials;
    const adminDetails = findSettingsPermissionGroup(this.rolePermissions, SETTINGS_MODULE_TYPES);
    const index = adminDetails?.permissions?.findIndex((obj: any) => obj.module == 'Tutorials');
    if (index != undefined && index != -1) {
      canViewTutorials = adminDetails.permissions[index].canView;
    }
    return canViewTutorials;
  }

  canAccessNotifications() {
    this.loadRolePermissions();
    let canViewNotifications, canEditNotification, canDeleteNotification;
    const adminDetails = findSettingsPermissionGroup(this.rolePermissions, SETTINGS_MODULE_TYPES);
    const index = adminDetails?.permissions?.findIndex((obj: any) => obj.module == 'Notifications');
    if (index != undefined && index != -1) {
      canViewNotifications = adminDetails.permissions[index].canView;
      canEditNotification = adminDetails.permissions[index].canEdit;
      canDeleteNotification = adminDetails.permissions[index].canDelete;
    }
    return { canViewNotifications, canEditNotification, canDeleteNotification };
  }

  /**
   * Parent Reports access after the permission split.
   * True when any of the five report leaves (or legacy "Reports") is viewable.
   */
  canAccessReports(): boolean {
    const reportsDetails = this.getReportsPermissionGroup();
    if (!reportsDetails?.permissions?.length) {
      return false;
    }
    return reportsDetails.permissions.some(
      (permission: { module?: string; canView?: boolean }) =>
        !!permission?.canView &&
        (AccessControlService.REPORT_ENTRY_MODULES as readonly string[]).includes(
          permission.module ?? ''
        )
    );
  }

  canViewAccountStatements(): boolean {
    return this.canViewReportModule('Account Statements');
  }

  canViewActivityExport(): boolean {
    return this.canViewReportModule('Activity Export');
  }

  canViewOutstandingStatement(): boolean {
    return this.canViewReportModule('Outstanding Statement');
  }

  canViewInternalReports(): boolean {
    return this.canViewReportModule('Internal Reports');
  }

  /** Per-report submodule view check (supports legacy single "Reports" module). */
  canViewReportModule(moduleName: string): boolean {
    const reportsDetails = this.getReportsPermissionGroup();
    const exact = reportsDetails?.permissions?.find(
      (permission: { module?: string }) => permission.module === moduleName
    );
    if (exact) {
      return !!exact.canView;
    }
    const legacyReports = reportsDetails?.permissions?.find(
      (permission: { module?: string }) => permission.module === 'Reports'
    );
    return !!legacyReports?.canView;
  }

  /** First report route the user may open (for landing / REPORTS nav target). */
  getFirstReportsLandingPath(): string {
    for (const moduleName of AccessControlService.REPORT_ENTRY_MODULES) {
      if (this.canViewReportModule(moduleName)) {
        return (
          AccessControlService.REPORT_LANDING_PATH_BY_MODULE[moduleName] ||
          'report-account-statements'
        );
      }
    }
    return 'report-account-statements';
  }

  canAccessProjections() {
    let canViewProjections = false,
      canAddProjections = false,
      canEditProjections = false,
      canDeleteProjections = false;
    const reportsDetails = this.getReportsPermissionGroup();
    const index = reportsDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Projections'
    );
    if (index != undefined && index != -1) {
      canViewProjections = !!reportsDetails.permissions[index].canView;
      canAddProjections = !!reportsDetails.permissions[index].canAdd;
      canEditProjections = !!reportsDetails.permissions[index].canEdit;
      canDeleteProjections = !!reportsDetails.permissions[index].canDelete;
    }
    return {
      canViewProjections,
      canAddProjections,
      canEditProjections,
      canDeleteProjections,
    };
  }

  canViewProjections(): boolean {
    return !!this.canAccessProjections().canViewProjections;
  }

  isInternalRoleUser(): boolean {
    try {
      const details = JSON.parse(this.localStoragePort.getItem('UserRoleDetails') || '{}');
      return Number(details?.roleType) === RoleType.Internal;
    } catch {
      return false;
    }
  }

  private getReportsPermissionGroup(): any {
    this.loadRolePermissions();
    return (
      this.rolePermissions.find((group: any) => group.header === 'Reports') ||
      this.rolePermissions.find((group: any) =>
        group.permissions?.some(
          (permission: { module?: string }) =>
            permission.module === 'Reports' ||
            permission.module === 'Account Statements' ||
            permission.module === 'Projections'
        )
      ) ||
      this.rolePermissions[4]
    );
  }

  private loadRolePermissions() {
    this.rolePermissions = JSON.parse(this.localStoragePort.getItem('RolePermissions') || '[]');
  }

  private loadSupplierRolePermissions() {
    this.supplierRolePermissions = JSON.parse(
      this.localStoragePort.getItem('SupplierRolePermissions') || '[]'
    );
  }
}
